import { useMemo, useState } from "react";
import {
  SlidersHorizontal,
  Building2,
  Bot,
  Shield,
  CreditCard,
  Check,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem } from "@/components/brand/Hero";
import { Pill } from "@/components/brand/Pill";
import { Mantra, MantraAccent } from "@/components/brand/Card";
import { SettingsShell, FieldCard, Field } from "@/components/brand/SettingsShell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { errorDetailOr } from "@/lib/api-error";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import {
  useWorkspaceSettings,
  useUpdateWorkspaceSettings,
  useAdminUsers,
} from "@/hooks/useApi";
import type { UpdateWorkspaceSettingsPayload } from "@/api/admin-workspace";

interface PlanCard {
  label: string;
  title: string;
  price: string;
  desc: string;
  key: string;
}

// Permanent mock: plan/pricing tiers come from a billing provider integration
// (Stripe etc). Out of scope for the views-wiring series; surfaces "coming soon".
//
// ⚠️ Nenhuma linha aqui pode declarar como fato algo que não existe — mesmo
// critério que tirou "SOC 2" do desc do Tier 2 (achado de review pós-SIMP.4):
// "· current" marcava um tier como o contrato vigente do workspace, e
// "$0.035 / 1K tok" / "24/7 support" liam como termos comerciais reais, sem
// billing nenhum por trás. Preço e suporte saíram; o tier continua listado
// como preview, só não afirma mais nada sobre o contrato de ninguém.
const PLANS: PlanCard[] = [
  { label: "Team", title: "Team", price: "$0.05 / 1K tok", desc: "Up to 10 seats · 5 projects · community support", key: "team" },
  { label: "Enterprise · Tier 2", title: "Enterprise · Tier 2", price: "—", desc: "Pricing not finalized.", key: "tier2" },
  { label: "Enterprise · Tier 3", title: "Enterprise · Tier 3", price: "Custom", desc: "Unlimited · dedicated region · BYOK · private model weights", key: "tier3" },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AdminSettings() {
  const { t } = useTranslation();
  const { data: settings } = useWorkspaceSettings();
  const updateMutation = useUpdateWorkspaceSettings();
  const { toast } = useToast();

  /**
   * Um rascunho só, cobrindo os cards de Organização e de Padrões da plataforma,
   * para que o "Save changes" do rodapé descarregue os dois juntos.
   *
   * ⚠️ **Sem reset por efeito.** Havia um `useEffect(..., [settings])` que
   * zerava o rascunho a cada objeto novo de `settings`. Ele era redundante desde
   * sempre — o estado nasce `{}` e o `handleSave` o limpa depois de gravar — e
   * virou destrutivo quando o card de SSO passou a ser um segundo escritor da
   * página: o `PUT` dele invalida `["admin","workspace-settings"]`, o refetch
   * devolve uma linha com o `sso_enabled` trocado, e o efeito apagava o que a
   * pessoa tinha digitado nos dois formulários acima, sem mensagem nenhuma.
   *
   * Nada é perdido com a remoção, porque os campos renderizam o rascunho por
   * cima do servidor: o que ninguém tocou continua seguindo o servidor a cada
   * refetch, e só o que foi editado fica por cima. Um valor mudado por outro
   * admin aparece no campo intocado — e não sobrescreve a edição em
   * andamento, que é o comportamento que se espera de formulário com trabalho
   * não salvo.
   *
   * ⚠️ **"Por cima" não é `draft.x ?? settings?.x`.** `workspace_name` e
   * `budget_cap_usd` são campos que o admin pode limpar de propósito — o hint
   * do cap de orçamento é explícito: "Empty = unlimited".
   * Limpar grava `null` no rascunho, e `??` trata `null` exatamente como
   * "não tocado": o merge caía de volta pro valor do servidor, o campo
   * reaparecia preenchido com `dirty: true` e o Save habilitado, e salvar
   * mandava o valor antigo de volta — o esvaziamento nunca acontecia. O
   * conserto é perguntar se a chave está no rascunho, não se o valor é
   * nullish: `"workspace_name" in draft` é `true` mesmo com
   * `draft.workspace_name === null`, e só aí o merge usa o rascunho.
   *
   * Primary domain, timezone, idioma e aprovação humana saíram da tela (#1042):
   * eram gravados e nada os lia. As colunas seguem no banco e na API; como não
   * há campo que escreva no rascunho, nenhum deles volta a viajar no `PUT`.
   */
  const [draft, setDraft] = useState<UpdateWorkspaceSettingsPayload>({});

  const merged = useMemo(() => {
    return {
      workspace_name:
        "workspace_name" in draft
          ? (draft.workspace_name ?? "")
          : (settings?.workspace_name ?? ""),
      budget_cap_usd:
        "budget_cap_usd" in draft
          ? (draft.budget_cap_usd ?? null)
          : (settings?.budget_cap_usd ?? null),
    };
  }, [draft, settings]);

  const dirty = Object.keys(draft).length > 0;
  // `null`, não um `key` de tier: nenhum plano é o "vigente" do workspace —
  // não tem billing por trás para dizer qual é. Pré-selecionar um faria o
  // card afirmar, na primeira renderização, o mesmo tipo de fato inventado
  // que "· current" afirmava por texto.
  const [activePlan, setActivePlan] = useState<string | null>(null);

  /**
   * O texto exatamente como foi digitado no cap de orçamento — `null` quando
   * ninguém está editando (a caixa segue `merged.budget_cap_usd`).
   *
   * ⚠️ **Sem isto, o input controlado comia o ponto decimal.** O `value` da
   * caixa vinha de `String(merged.budget_cap_usd)`, um número já passado por
   * `parseFloat` no keystroke anterior. Digitar "100." fazia
   * `parseFloat("100.")` virar `100`, o estado virar `100`, e o React
   * reescrever a caixa como "100" — o ponto sumia antes do dígito seguinte
   * chegar, e "100.50" nunca se formava (virava "10050"). Guardar o texto
   * digitado à parte do número parseado quebra esse ciclo: a caixa mostra
   * exatamente o que foi digitado, e só o valor válido mais recente vai para
   * o rascunho.
   */
  const [budgetText, setBudgetText] = useState<string | null>(null);

  /**
   * Tela inteiramente admin — o gate é de rota, como nas outras `/admin/*`.
   *
   * Até aqui ela era a exceção: recebia qualquer usuário porque hospedava o
   * formulário de troca de senha, e por dentro escondia card a card o que ele não
   * podia salvar. O formulário mudou para `/profile`, que é a tela da pessoa, e
   * com isso o motivo da exceção acabou. Não há mais desvio por papel aqui
   * dentro: o que sobrou é configuração de workspace, e `PUT /api/admin/workspace`
   * é `get_current_admin`.
   */
  const handleSave = async () => {
    if (!dirty) return;
    try {
      await updateMutation.mutateAsync(draft);
      setDraft({});
      setBudgetText(null);
      toast({
        title: "Settings saved",
        description: "Your workspace settings have been updated.",
      });
    } catch (err: unknown) {
      toast({
        title: "Save failed",
        description: errorDetailOr(err, "Failed to save settings."),
        variant: "destructive",
      });
    }
  };

  const handleDiscard = () => {
    setDraft({});
    setBudgetText(null);
  };

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<SlidersHorizontal />}
        overline={t("ADMIN · WORKSPACE CONFIG")}
        title={t("Settings")}
        meta={
          <HeroMetaItem k="Workspace" v={merged.workspace_name || t("Untitled")} />
        }
        kpis={[
          {
            label: t("Budget cap"),
            value:
              merged.budget_cap_usd != null
                ? `$${merged.budget_cap_usd.toFixed(0)}`
                : "—",
            sub: t("per project default"),
          },
          // KPI mock com rótulo explícito, espelhando o card de billing logo
          // abaixo — os dois existem, então o KPI leva a algo real ao clicar
          // no card correspondente.
          //
          // ⚠️ O par "Compliance" saiu daqui (achado de revisão pós-SIMP.4).
          // A tentativa anterior de justificá-lo — "promete algo vindouro",
          // não "afirma certificação" — era distinção de comentário, não da
          // regra que a nav já codifica (`SettingsShell.tsx`: todo rótulo
          // leva a uma seção renderizada). Um KPI nomeando "Compliance" sem
          // seção nenhuma atrás é o mesmo beco sem saída que tirou Branding e
          // API keys da nav — só que fora do array que o teste cobria.
          { label: t("Plan"), value: "—", unit: t("Coming soon"), sub: t("billing TBD") },
        ]}
      />

      <div className="p-8">
        {/* A navegação da esquerda lista exatamente os cards renderizados: um
            item de nav por seção, rótulo idêntico ao título do card (SIMP.4
            #425). Item sem card atrás destaca a linha e não mostra nada de
            novo ao clicar. Branding e API keys saíram junto (#425) e não têm
            trabalho rastreado; se virarem escopo, entram como issue, não
            como item de nav. */}
        <SettingsShell
          groups={[
            {
              header: t("WORKSPACE"),
              items: [
                { id: "organization", label: t("Organization"), icon: <Building2 /> },
                { id: "platform-defaults", label: t("Platform defaults"), icon: <Bot /> },
              ],
            },
            {
              header: t("IDENTITY"),
              items: [
                // Não é SAML: a integração é OIDC com um IdP só, registrado
                // nos env vars `ENTRA_*` (ADR de 2026-08-17).
                { id: "single-sign-on", label: t("Single sign-on"), icon: <Shield /> },
              ],
            },
            {
              header: t("BILLING"),
              items: [{ id: "plan-billing", label: t("Plan & billing"), icon: <CreditCard /> }],
            },
          ]}
          // `id` estável, não o rótulo traduzido: achado de review — o rótulo
          // muda com `i18n.changeLanguage`, e a âncora não pode.
          defaultActive="organization"
        >
            {/* ------------------------------------------------------------ */}
            {/* Organization (wired) */}
            {/* ------------------------------------------------------------ */}
            <FieldCard
              id="organization"
              icon={<Building2 />}
              title={t("Organization")}
              sub={t("How myGROW identifies this workspace.")}
            >
              <Field label={t("Workspace name")}>
                <ControlledInput
                  value={merged.workspace_name}
                  placeholder={t("Untitled workspace")}
                  onChange={(v) =>
                    setDraft((d) => ({ ...d, workspace_name: v || null }))
                  }
                />
              </Field>
            </FieldCard>

            {/* ------------------------------------------------------------ */}
            {/* Platform defaults (wired — budget) */}
            {/* ------------------------------------------------------------ */}
            <FieldCard
              id="platform-defaults"
              icon={<Bot />}
              title={t("Platform defaults")}
              sub={t("Applied to every new project in this workspace.")}
            >
              <Field
                label={t("Project budget cap")}
                hint={t("Default monthly hard stop per project. Empty = unlimited.")}
              >
                <ControlledInput
                  value={
                    budgetText ??
                    (merged.budget_cap_usd != null ? String(merged.budget_cap_usd) : "")
                  }
                  placeholder="100.00"
                  mono
                  style={{ maxWidth: 160 }}
                  onChange={(v) => {
                    setBudgetText(v);
                    if (v.trim() === "") {
                      setDraft((d) => ({ ...d, budget_cap_usd: null }));
                    } else {
                      const n = parseFloat(v);
                      if (!Number.isNaN(n) && n >= 0) {
                        setDraft((d) => ({ ...d, budget_cap_usd: n }));
                      }
                    }
                  }}
                />
              </Field>
            </FieldCard>

            {/* ------------------------------------------------------------ */}
            {/* Single sign-on (wired — independent save flow) */}
            {/* ------------------------------------------------------------ */}
            <SsoCard id="single-sign-on" />

            {/* ------------------------------------------------------------ */}
            {/* MOCKED — coming soon */}
            {/* ------------------------------------------------------------ */}

            <FieldCard
              id="plan-billing"
              icon={<CreditCard />}
              title={t("Plan & billing")}
              sub={t("Plan tier selection.")}
              pill={<Pill tone="muted">{t("Coming soon")}</Pill>}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PLANS.map((p) => {
                  const on = p.key === activePlan;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setActivePlan(p.key)}
                      className={cn(
                        "border rounded-[10px] p-4 cursor-pointer bg-white text-left",
                        on
                          ? "border-[#ffc83d] shadow-[inset_0_0_0_1px_#ffc83d] bg-[rgba(255,200,61,0.04)]"
                          : "border-[#e8ebf0]",
                      )}
                    >
                      <div className="font-mono text-[10px] text-[#7b8799] tracking-[0.12em] uppercase font-semibold">
                        {p.label}
                      </div>
                      <div className="font-brand font-extrabold text-xl text-[#08254c] mt-1 mb-0.5 tracking-[-0.02em]">
                        {p.price}
                      </div>
                      <div className="text-xs text-[#596b85]">{p.desc}</div>
                    </button>
                  );
                })}
              </div>
            </FieldCard>

            <div className="sticky bottom-0 pt-5 pb-2 flex justify-end gap-2.5 bg-gradient-to-b from-transparent to-[#f7f7f7]">
              <Button variant="outline" onClick={handleDiscard} disabled={!dirty}>
                {t("Discard")}
              </Button>
              <Button
                variant="action"
                className="gap-2"
                onClick={handleSave}
                disabled={!dirty || updateMutation.isPending}
              >
                <Check className="w-3.5 h-3.5" />{" "}
                {updateMutation.isPending ? t("Saving…") : t("Save changes")}
              </Button>
            </div>
        </SettingsShell>

        <Mantra>
          WORKSPACE · <MantraAccent>{t("YOUR CONFIG")} · {t("YOUR CONTROL")}</MantraAccent> · {t("INSTANT REVOCATION")}
        </Mantra>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controlled inputs — wrap raw <input>/<select> with the brand visual style.
// The brand TextInput / Select were uncontrolled (defaultValue, no onChange),
// so these tiny controlled variants were inlined here instead. Those brand
// controls were removed in #1044 for lack of use; these are the ones in use.
// ---------------------------------------------------------------------------

function ControlledInput({
  value,
  placeholder,
  onChange,
  mono,
  style,
  type = "text",
  disabled,
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  mono?: boolean;
  style?: React.CSSProperties;
  type?: "text" | "email" | "password";
  disabled?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={cn(
        "w-full h-9 px-3 border border-[#d4dae3] rounded-md bg-white text-[13px] text-[#08254c] font-[inherit]",
        "focus:outline-0 focus:border-[#ffc83d] focus:ring-[3px] focus:ring-[rgba(255,200,61,0.15)]",
        disabled && "bg-[#f7f8fb] text-[#7b8799]",
        mono && "font-mono text-xs",
      )}
      style={style}
    />
  );
}

/**
 * Interruptor controlado, com `role="switch"`.
 *
 * O `Switch` de `SettingsShell` guarda o próprio estado e não avisa ninguém —
 * serve aos cards mockados, não a um que salva. Este é o par controlado, e o
 * `role`/`aria-checked` existem porque um `<button>` liso não diz a leitor de
 * tela nenhum se está ligado ou desligado.
 */
function ControlledSwitch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "w-9 h-5 rounded-full relative shrink-0 transition-colors",
        checked ? "bg-[#ffc83d]" : "bg-[#d4dae3]",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
      )}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.15)] transition-[left]"
        style={{ left: checked ? "18px" : "2px" }}
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Single sign-on — fluxo de save próprio, e isso é o ponto do card.
//
// `sso_enabled` **não** entra no `draft` da página junto com nome e budget. A
// guarda de habilitação do backend não é transicional
// (`admin_workspace.py:315`): reafirmar `true` exige `entra_oid` no chamador.
// Se a flag viajasse no mesmo PUT do formulário, um admin sem vínculo tomaria
// `400` ao renomear o workspace — um erro sobre SSO num campo que não é de SSO.
// Um PUT, um campo.
// ---------------------------------------------------------------------------

function SsoCard({ id }: { id?: string }) {
  const { t } = useTranslation();
  const { data: settings } = useWorkspaceSettings();
  const mutation = useUpdateWorkspaceSettings();
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const enabled = settings?.sso_enabled === true;

  /**
   * O roster, e **só enquanto o diálogo de desligar estiver aberto**.
   *
   * O `enabled` daqui não é sobre permissão — a tela é admin por rota, e ler o
   * roster exige `manager`, então a chamada passaria. É sobre custo:
   * `useAdminUsers` tem `refetchInterval: 60_000`, e o único consumidor deste
   * dado é lido **dentro** do `<AlertDialog>`, que fica fechado quase o tempo
   * todo. Sem isto, deixar a aba aberta em `/admin/settings` buscaria o roster
   * inteiro uma vez por minuto, para sempre.
   *
   * ⚠️ **E por isso o botão de desligar espera o roster.** Sem ele o número não
   * renderiza, e o diálogo fica com aparência de completo — sem nada sinalizando
   * que falta informação a caminho. Como o `AlertDialogAction` do Radix fecha o
   * diálogo no clique, o aviso ia junto: não havia nem segunda chance de ler o
   * que se acabou de fazer. `isLoading` guarda o botão, e `isError` **não**:
   * falha de rede não pode trancar o admin fora da própria ação.
   */
  const { data: roster, isLoading: rosterLoading, isError: rosterFailed } = useAdminUsers({
    enabled: confirming && enabled,
  });

  /**
   * Quantas contas ativas perdem o acesso se o SSO for desligado agora: **todas**.
   *
   * Isto já foi um cálculo. Enquanto existia login por senha, desligar o SSO
   * deixava de pé quem tinha senha utilizável — as duas colunas, não só a flag —,
   * e o número era quem sobrava sem porta. Havia também um `canBeUndone`, que
   * perguntava se ainda restaria um admin capaz de religar pela aplicação: com
   * senha, vinculado ao diretório, e ativo.
   *
   * Com o Entra como única porta não há o que calcular. `sso_enabled = FALSE`
   * desliga o `_RESOLVE_BY_OID` (`JOIN mygrow.tenants ... AND t.sso_enabled =
   * TRUE`), e não sobra caminho nenhum — nem para quem desligou. **A volta é um
   * `UPDATE` no banco.**
   *
   * O backend não avisa: a guarda dele recusa quando *ninguém* está vinculado, o
   * que era proteção de reversibilidade num mundo com duas portas. Hoje ela passa
   * exatamente nos casos perigosos.
   */
  const lockedOut = useMemo(
    () => (roster?.users ?? []).filter((u) => u.is_active).length,
    [roster],
  );

  const apply = async () => {
    setErrorMsg(null);
    try {
      await mutation.mutateAsync({ sso_enabled: !enabled });
      toast({
        title: t("Single sign-on updated"),
        description: enabled
          ? t("Single sign-on is now off for this workspace.")
          : t("Single sign-on is now on for this workspace."),
      });
    } catch (err: unknown) {
      // O `detail` do backend é pt-BR e explica a guarda inteira em três frases
      // (S-069). Vai para a tela verbatim, e num bloco fixo — toast some antes
      // de alguém terminar de ler.
      setErrorMsg(errorDetailOr(err, t("Could not change single sign-on.")));
    }
  };

  return (
    <FieldCard
      id={id}
      icon={<Shield />}
      title={t("Single sign-on")}
      sub={t("Who can sign in through the Microsoft directory.")}
      pill={<Pill tone={enabled ? "ok" : "muted"}>{enabled ? t("On") : t("Off")}</Pill>}
    >
      <Field
        label={t("Provider")}
        hint={t("One directory per installation, configured in the server environment.")}
      >
        <div className="h-9 flex items-center text-[13px] text-[#08254c] font-medium">
          Microsoft Entra ID
        </div>
      </Field>

      <Field label={t("Status")}>
        <div className="inline-flex items-center gap-2.5">
          <ControlledSwitch
            checked={enabled}
            label={t("Single sign-on")}
            disabled={!settings || mutation.isPending}
            onChange={() => setConfirming(true)}
          />
          <span className="text-[13px] text-[#08254c]">
            {enabled
              ? t("On · people can sign in with Microsoft")
              : t("Off · the Microsoft button does not work here")}
          </span>
        </div>
      </Field>

      {errorMsg && (
        <div className="text-xs text-[#d4353c] bg-[rgba(212,53,60,0.08)] border border-[rgba(212,53,60,0.2)] rounded px-2.5 py-2">
          {errorMsg}
        </div>
      )}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {enabled ? t("Turn off single sign-on?") : t("Turn on single sign-on?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {enabled
                ? t(
                    "Microsoft is the only way into this workspace, so turning it off locks out everyone — you included, as soon as this session expires. There is no way to turn it back on from the app.",
                  )
                : t(
                    "People in the directory start signing in through it. Someone who has never signed in before is created on their first visit, with the User role.",
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {enabled && rosterLoading && (
            <p role="status" className="text-xs text-[#596b85]">
              {t("Counting who would lose access...")}
            </p>
          )}
          {enabled && rosterFailed && (
            <div className="flex items-start gap-2 text-xs text-[#b02a31] bg-[rgba(212,53,60,0.08)] border border-[rgba(212,53,60,0.2)] rounded px-2.5 py-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
              <span>
                {t(
                  "Could not count who would lose access. Turning this off locks the workspace either way, and only a database change brings it back.",
                )}
              </span>
            </div>
          )}
          {enabled && lockedOut > 0 && (
            <div className="flex flex-col gap-1.5 text-xs text-[#b02a31] bg-[rgba(212,53,60,0.08)] border border-[rgba(212,53,60,0.2)] rounded px-2.5 py-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                <span>
                  {t("Active accounts that lose access right now: {{n}}.", { n: lockedOut })}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                <span>
                  {t(
                    "Nobody can turn this back on from the app, because nobody can sign in. Recovery means going into the database.",
                  )}
                </span>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={apply} disabled={enabled && rosterLoading}>
              {enabled ? t("Turn off") : t("Turn on")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FieldCard>
  );
}
